<script setup lang="ts">
import { computed } from 'vue';
import SuperSection from '~/components/SuperSection.vue';

//
// --- Types (adapted to your generated interfaces) ---
//

type OneOf<T> = T | string | null | undefined;

interface SectionsBlock {
  id: number;
  collection?: string | null;
  item?: Record<string, any> | string | null;
  hide_block?: boolean | null;
  background_color: string;
}

interface Section {
  id: number | string;
  side?: 'right' | 'left' | null;
  sticky?: boolean | null;
  svg_inline?: string | null;
  Color_1?: string | null;
  Color_2?: string | null;
  Color_3?: string | null;
  Color_4?: string | null;
}

interface PagesSectionPlacement {
  id: number | string;
  sort?: number | null;
  side_override?: 'right' | 'left' | 'Null' | null;
  Color_1_override?: string | null;
  Color_2_override?: string | null;
  Color_3_override?: string | null;
  Color_4_override?: string | null;
  sticky_override?: 'true' | 'false' | 'NULL' | null;
  blocks?: SectionsBlock[] | string[];
  // API may return one of the following keys depending on your fields query:
  item?: OneOf<Section>; // when you used `sections.item:sections.*` but SDK flattened to `item`
  ['item:sections']?: OneOf<Section>; // when the alias key is kept verbatim
}

interface PageBuilderProps {
  // Pass the array you get from `page.sections` (the placements)
  sections: PagesSectionPlacement[];
}

const props = defineProps<PageBuilderProps>();

//
// --- Helpers ---
//

// safely pick the expanded Section object from the placement (supports both alias shapes)
function pickSectionObject(p: PagesSectionPlacement): Section | null {
  const a = (p as any)['item:sections'];
  if (a && typeof a === 'object') return a as Section;
  const b = (p as any).item;
  if (b && typeof b === 'object') return b as Section;
  return null;
}

// Merge placement overrides over base section fields and normalize blocks
function resolveSectionVM(p: PagesSectionPlacement) {
  const s = pickSectionObject(p);

  // If nothing expanded, return a minimal placeholder so UI doesn't explode
  if (!s) {
    return {
      id: p.id,
      side: p.side_override ?? 'right',
      // stick override is a string, so we need to normalize it (if true or false, it should be the corresponding boolean, otherwise false)
      sticky: p.sticky_override === 'true' ? true : p.sticky_override === 'false' ? false : true,
      svg_inline: null,
      Color_1: p.Color_1_override ?? null,
      Color_2: p.Color_2_override ?? null,
      Color_3: p.Color_3_override ?? null,
      Color_4: p.Color_4_override ?? null,
    };
  }

  // Normalize blocks: keep only blocks with a real object in `item`
  const rawBlocks = Array.isArray(p.blocks) ? (p.blocks as any[]) : [];
  const blocks = rawBlocks
    .filter((b) => b && typeof b === 'object' && typeof b.collection === 'string')
    .filter((b) => b.item && typeof b.item === 'object' && !b.hide_block);

  return {
    id: p.id, // per-placement id keeps v-for keys stable
    side: p.side_override ?? s.side ?? 'right',
    sticky: p.sticky_override === 'true' ? true : p.sticky_override === 'false' ? false : (s.sticky ?? true), // default to true if not specified
    svg_inline: (p as any).svg_inline_override ?? s.svg_inline ?? null,
    Color_1: p.Color_1_override ?? s.Color_1 ?? null,
    Color_2: p.Color_2_override ?? s.Color_2 ?? null,
    Color_3: p.Color_3_override ?? s.Color_3 ?? null,
    Color_4: p.Color_4_override ?? s.Color_4 ?? null,
    blocks,
  };
}

const resolvedSections = computed(() => (props.sections ?? []).map(resolveSectionVM));
</script>

<template>
  <div v-if="resolvedSections.length === 0" class="py-12 text-center text-sm text-neutral-500">
    No sections available
  </div>

  <div v-else>
    <SuperSection v-for="section in resolvedSections" :key="section.id" :section="section">
      <template #default="{ section: s }">
        <div
          v-for="(block, i) in s.blocks"
          :key="block.id"
          class="[background:var(--bg)] w-full"
          :class="{ 'pb-20': i === s.blocks.length - 1 }"
          :style="block.background_color ? { '--bg': block.background_color } : { '--bg': 'transparent' }"
        >
          <Container>
            <BaseBlock :block="block" />
          </Container>
        </div>
      </template>
    </SuperSection>
  </div>
</template>
